import { cn } from "@base/lib/utils";
import { useCurrentUserQuery } from "@base/model/hooks";
import { AppLink } from "@base/ui/AppLink";
import { projectsQueryOptions } from "@features/home/model/useProjectsQuery";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { FolderKanban, House, ImageOff } from "lucide-react";

const PROJECT_PATH = /^\/projects\/(\d+)(?:\/|$)/;

/** 768px 이상에서만 보이는 좌측 내비게이션 */
export function BricksRail() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const currentProjectId = PROJECT_PATH.exec(pathname)?.[1] ?? null;
  const isHome = pathname === "/";
  const isProjects = pathname.startsWith("/projects");

  return (
    <nav aria-label="Main" className="flex flex-col h-full">
      <div className="flex gap-3 items-center px-5 pt-6 pb-4">
        <img src="/assets/images/app/bricks_logo.webp" alt="" className="w-8 h-auto" />
        <span className="text-title6 text-gray-1000">Bricks</span>
      </div>

      <ul className="flex flex-col gap-1 px-3">
        <li>
          <RailLink to="/" isActive={isHome} icon={<House width={18} height={18} aria-hidden="true" />}>
            Home
          </RailLink>
        </li>
        <li>
          <RailLink
            to="/projects"
            isActive={isProjects && currentProjectId === null}
            icon={<FolderKanban width={18} height={18} aria-hidden="true" />}
          >
            Projects
          </RailLink>
        </li>
      </ul>

      <RailProjects currentProjectId={currentProjectId} />
      <RailCurrentUser />
    </nav>
  );
}

interface RailLinkProps {
  to: "/" | "/projects";
  isActive: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function RailLink({ to, isActive, icon, children }: RailLinkProps) {
  return (
    <AppLink
      to={to}
      direction="none"
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex gap-3 items-center px-3 py-2 rounded-8 text-body3",
        isActive ? "bg-blue-10 text-blue-600" : "text-gray-900 hover:bg-gray-100",
      )}
    >
      {icon}
      {children}
    </AppLink>
  );
}

/** 프로젝트 목록 실패는 rail 안에서만 보여 준다. 인증 흐름의 필수 조건으로 만들지 않는다 */
function RailProjects({ currentProjectId }: { currentProjectId: string | null }) {
  const { data, isLoading, isError, refetch } = useQuery(projectsQueryOptions());

  return (
    <section aria-labelledby="rail-projects-heading" className="flex overflow-y-auto flex-col flex-1 px-3 mt-6 min-h-0">
      <h2
        id="rail-projects-heading"
        className="px-3 mb-2 font-semibold tracking-wide text-label3 text-gray-500 uppercase"
      >
        My projects
      </h2>
      {isLoading && <p className="px-3 text-label3 text-gray-600">Loading projects…</p>}
      {isError && (
        <p className="px-3 text-label3 text-gray-700" role="alert">
          Couldn't load projects.{" "}
          <button type="button" className="text-blue-600 underline" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      )}
      <ul className="flex flex-col gap-1">
        {data?.projects.map((project) => {
          const isCurrent = String(project.id) === currentProjectId;
          return (
            <li key={project.id}>
              {/* 다른 프로젝트로 가는 링크는 현재 검색 조건을 물려주지 않는다 */}
              <AppLink
                to="/projects/$projectId"
                params={{ projectId: String(project.id) }}
                direction="none"
                aria-current={isCurrent ? "page" : undefined}
                title={project.name}
                className={cn(
                  "flex gap-2 items-center px-3 py-2 rounded-8 text-body4",
                  isCurrent ? "bg-gray-100 text-gray-1000" : "text-gray-900 hover:bg-gray-100",
                )}
              >
                {project.thumbnail_image_url ? (
                  <img src={project.thumbnail_image_url} alt="" className="object-cover size-6 rounded-2 shrink-0" />
                ) : (
                  <span className="flex justify-center items-center bg-gray-100 size-6 rounded-2 shrink-0">
                    <ImageOff width={12} height={12} aria-hidden="true" className="text-gray-500" />
                  </span>
                )}
                <span className="truncate">{project.name}</span>
              </AppLink>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RailCurrentUser() {
  const { data } = useCurrentUserQuery();
  const user = data?.user;
  if (!user) return null;

  return (
    <div className="flex gap-3 items-center px-5 py-[19px] border-t border-gray-200">
      <span
        aria-hidden="true"
        className="flex justify-center items-center text-blue-600 bg-blue-10 size-8 rounded-circle text-label2 shrink-0"
      >
        {user.name.slice(0, 1).toUpperCase()}
      </span>
      <div className="flex flex-col min-w-0">
        <span className="truncate text-label2 text-gray-1000">{user.name}</span>
        <span className="truncate text-label3 text-gray-600">{user.email}</span>
      </div>
    </div>
  );
}
